import { feedbackAvailable, openFeedbackForm } from '../../../Services/Telemetry/Feedback.js'

interface Props {
  /** The Sentry event this crash was reported as. Shown so a support conversation can name it. */
  eventId?: string
  /** Offered only where the caller can actually re-render — the router's boundary cannot. */
  onRetry?: () => void
}

/**
 * The screen a user gets when a crash has swallowed whatever they were doing.
 *
 * Navigation is by plain anchor rather than `Link`: this also renders from the boundary *outside*
 * `RouterProvider`, where router hooks are not available, and a crash is one of the few cases where
 * discarding the current page state is the point.
 */
export const ErrorScreen = ({ eventId, onRetry }: Props) => (
  <div className="min-h-screen flex items-center justify-center p-6">
    <div className="max-w-xl w-full border border-gray-300 rounded-2xl bg-white shadow-lg p-8 text-center">
      <h1 className="text-2xl font-semibold mb-2">Something went wrong</h1>
      <p className="text-gray-600 mb-6">
        This part of the application stopped unexpectedly. The problem has been reported to us automatically — reloading usually gets you
        going again.
      </p>

      <div className="flex flex-wrap gap-3 justify-center">
        <button className="btn btn-primary" onClick={() => window.location.reload()}>
          Reload the page
        </button>
        {onRetry && (
          <button className="btn btn-ghost" onClick={onRetry}>
            Try again
          </button>
        )}
        <a className="btn btn-ghost" href="/">
          Back to start
        </a>
        {feedbackAvailable() && (
          <button className="btn btn-ghost" onClick={() => void openFeedbackForm(eventId)}>
            Tell us what happened
          </button>
        )}
      </div>

      {eventId && <p className="mt-6 text-xs text-gray-400 font-mono break-all">Reference: {eventId}</p>}
    </div>
  </div>
)
